/**
 * Swiper Configuration Helper
 * Provides reusable configurations for Swiper carousels
 */

/**
 * Creates a new Swiper instance with merged default and custom configurations
 * @param {string} selector - The CSS selector for the swiper container
 * @param {Object} customConfig - Custom configuration to override defaults
 * @returns {Swiper} Swiper instance
 */
export const createSwiper = (selector, customConfig = {}) => {
  const Swiper = window.Swiper;

  const defaultConfig = {
    slidesPerView: 1,
    spaceBetween: 20,
    loop: false,
  };

  const mergedConfig = {
    ...defaultConfig,
    ...customConfig,
    breakpoints: customConfig.breakpoints || {},
  };

  return new Swiper(selector, mergedConfig);
};

/**
 * Preset configurations for common carousel types
 */
export const swiperPresets = {
  /**
   * Standard content carousel with responsive breakpoints
   */
  standardCarousel: {
    breakpoints: {
      320: {
        slidesPerView: 1,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 30,
      },
      1024: {
        slidesPerView: 3,
        spaceBetween: 30,
      },
    },
  },

  /**
   * Logo carousel with infinite scroll
   */
  logoCarousel: {
    loop: true,
    // speed: 6000,
    autoplay: {
      delay: 2000,
      disableOnInteraction: true,
    },

    autoHeight: false,
    // autoplay: {
    //   delay: 0,
    //   disableOnInteraction: true,
    // },
    // allowTouchMove: true,
    breakpoints: {
      375: {
        slidesPerView: 1,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 30,
      },
      1024: {
        slidesPerView: 5,
        spaceBetween: 30,
      },
      1440: {
        slidesPerView: 6,
        spaceBetween: 30,
      },
    },
  },

  /**
   * Single slide carousel with navigation
   */
  singleSlide: {
    slidesPerView: 1,
    spaceBetween: 20,
    loop: false,
  },

  /**
   * Pagination carousel
   */
  paginationCarousel: {
    slidesPerView: 1,
    spaceBetween: 20,
    loop: false,
    pagination: {
      el: ".swiper-pagination",
      type: "bullets",
      clickable: true,
    },
  },

  /**
   * Timeline slider carousel
   */
  timelineSlider: {
    slidesPerView: 1,
    spaceBetween: 0,
    loop: false,
    pagination: {
      el: ".swiper-pagination",
      type: "bullets",
      clickable: true,
    },
  },

  /**
   * Card SDG carousel without autoplay
   */
  cardSDGCarousel: {
    breakpoints: {
      320: {
        slidesPerView: 1,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 30,
      },
      1024: {
        slidesPerView: 3,
        spaceBetween: 30,
      },
    },
  },
};

/**
 * Creates a Swiper instance with a preset configuration
 * @param {string} selector - The CSS selector for the swiper container
 * @param {string} presetName - Name of the preset (e.g., 'standardCarousel')
 * @param {Object} customConfig - Additional custom configuration
 * @returns {Swiper} Swiper instance
 */
export const createSwiperWithPreset = (
  selector,
  presetName,
  customConfig = {}
) => {
  const preset = swiperPresets[presetName] || {};
  const mergedConfig = { ...preset, ...customConfig };
  return createSwiper(selector, mergedConfig);
};
